import { useState, useRef, useEffect, useId } from 'react';
import { X, ChevronDown, Check } from 'lucide-react';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';

interface Option {
    id: string;
    name: string;
}

interface Props {
    options: Option[];
    values: string[];
    onChange: (values: string[]) => void;
    placeholder: string;
    className?: string;
    maxChips?: number;
    /** The trigger's id, so a `<label htmlFor>` can name it. */
    id?: string;
    /** Accessible name when no visible label points at the trigger. Defaults to the placeholder. */
    ariaLabel?: string;
}

/** How many options are rendered at most; the search narrows the rest. */
const MAX_SHOWN = 100;

/**
 * A multi-select with a search box — the multi-value sibling of `SearchableSelect`, built to the
 * same keyboard contract (design.md §6; strategic KPIs round 2, K3):
 *
 * - The trigger is a real `<button>`. The chips sit beside it, not inside it — each chip has its
 *   own remove button, and a button cannot contain another one. "Clear all" is a button of its own.
 * - The list is a `listbox` with `aria-multiselectable`; the search box is its `combobox`. ↓/↑
 *   move through the options, Enter or Space toggles one and the list stays open (several picks
 *   in a row), Esc closes and returns focus to the trigger.
 */
export function MultiSearchableSelect({ options, values, onChange, placeholder, className, maxChips = 2, id, ariaLabel }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [active, setActive] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listId = useId();

    const selectedOptions = values.map(v => options.find(o => o.id === v)).filter((o): o is Option => o !== undefined);
    const filtered = search
        ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
        : options;
    const shown = filtered.slice(0, MAX_SHOWN);

    const close = (refocus: boolean) => {
        setIsOpen(false);
        setSearch('');
        setActive(0);
        if (refocus) triggerRef.current?.focus();
    };

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) close(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && inputRef.current) inputRef.current.focus();
    }, [isOpen]);

    // The highlighted option stays in view while ↑/↓ move it.
    useEffect(() => {
        if (!isOpen) return;
        document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' });
    }, [isOpen, active, listId]);

    // Esc closes the list, not the dialog the select sits in.
    useTopmostEscape(isOpen, () => close(true));

    const toggle = (optionId: string) => {
        onChange(values.includes(optionId) ? values.filter(v => v !== optionId) : [...values, optionId]);
    };

    const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, shown.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter' || (e.key === ' ' && search === '')) { e.preventDefault(); if (shown[active]) toggle(shown[active].id); }
    };

    const visibleChips = selectedOptions.slice(0, maxChips);
    const overflow = selectedOptions.length - visibleChips.length;

    const name = ariaLabel ?? placeholder;
    // The trigger's name says what it is AND what is chosen, the same contract SearchableSelect
    // uses — a label alone would go stale the moment a chip is added or removed.
    const triggerName = `${name}: ${selectedOptions.length === 0 ? 'none selected' : selectedOptions.map(o => o.name).join(', ')}`;

    return (
        <div ref={ref} className={`relative ${className || ''}`}>
            <div className="flex min-h-[32px] w-full items-center gap-1 rounded-md border bg-white pl-1 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500">
                {visibleChips.map(o => (
                    <span
                        key={o.id}
                        className="inline-flex max-w-[160px] shrink-0 items-center gap-0.5 rounded-full bg-blue-100 py-0.5 pl-2 pr-0.5 text-xs font-medium text-blue-800"
                        title={o.name}
                    >
                        <span className="truncate">{o.name}</span>
                        <button
                            type="button"
                            onClick={() => onChange(values.filter(v => v !== o.id))}
                            aria-label={`Remove ${o.name} from ${name}`}
                            className="rounded-full p-0.5 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        >
                            <X aria-hidden="true" className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                <button
                    ref={triggerRef}
                    id={id}
                    type="button"
                    onClick={() => (isOpen ? close(false) : setIsOpen(true))}
                    onKeyDown={e => { if (e.key === 'ArrowDown' && !isOpen) { e.preventDefault(); setIsOpen(true); } }}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? listId : undefined}
                    aria-label={triggerName}
                    className="flex min-h-[30px] min-w-0 flex-1 items-center justify-between gap-1 rounded-md px-1.5 text-left text-sm focus:outline-none"
                >
                    <span className="truncate text-xs text-gray-500">
                        {selectedOptions.length === 0 ? placeholder : overflow > 0 ? `+${overflow} more` : ''}
                    </span>
                    <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-gray-500" />
                </button>
                {selectedOptions.length > 0 && (
                    <button
                        type="button"
                        onClick={() => { onChange([]); triggerRef.current?.focus(); }}
                        aria-label={`Clear ${name}`}
                        className="mr-1 shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        <X aria-hidden="true" className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            {isOpen && (
                <div className="absolute top-full left-0 right-0 z-30 mt-1 min-w-[220px] rounded-lg border bg-white shadow-lg">
                    <div className="border-b p-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={e => { setSearch(e.target.value); setActive(0); }}
                            onKeyDown={onSearchKey}
                            placeholder="Search…"
                            role="combobox"
                            aria-expanded={true}
                            aria-autocomplete="list"
                            aria-label={`Search ${name}`}
                            aria-controls={listId}
                            aria-activedescendant={shown[active] ? `${listId}-${active}` : undefined}
                            autoComplete="off"
                            className="w-full rounded border px-2 py-1 text-xs"
                        />
                    </div>
                    <div id={listId} role="listbox" aria-multiselectable="true" aria-label={name} className="max-h-60 overflow-y-auto">
                        {filtered.length === 0 && (
                            <p className="py-3 text-center text-xs text-gray-500">No matches</p>
                        )}
                        {shown.map((o, i) => {
                            const isSelected = values.includes(o.id);
                            return (
                                <div
                                    key={o.id}
                                    id={`${listId}-${i}`}
                                    role="option"
                                    aria-selected={isSelected}
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => { toggle(o.id); setActive(i); }}
                                    onMouseEnter={() => setActive(i)}
                                    className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-xs ${i === active ? 'bg-blue-50' : ''} ${isSelected ? 'font-medium text-blue-800' : ''}`}
                                >
                                    <span className="truncate">{o.name}</span>
                                    {isSelected && <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-blue-600" />}
                                </div>
                            );
                        })}
                        {filtered.length > MAX_SHOWN && (
                            <p className="py-1 text-center text-[11px] text-gray-500">Showing the first {MAX_SHOWN} of {filtered.length}…</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
